"use client";

// ── Verdict rendering, shared by practice and contest (`EX-PUB01`/`EX-PUB08`) ─
// The contest read is `count_only` (`docs/29` §6.1) and the practice read is
// `full`, so the detail below renders whichever of the three blocks the policy
// left in the payload rather than assuming any of them. That is the whole
// difference between the two surfaces, and it lives here instead of in two
// components that would each have to remember it.

import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";

import { cn } from "@/lib/utils";
import type { JudgeVerdict, StudentTestResult } from "@/features/quiz/coding-task-api";
import { verdictTone } from "../../lib/format";
import { hasRunTwiceRows } from "../../lib/run-twice";
import { formatShare, partialShareOf } from "../../lib/partial-points";
import { RunTwiceDetail } from "./RunTwiceResult";

export type GradedStatus = "queued" | "running" | "completed";

/** The student-visible half of a graded submission, under any feedback policy. */
export type GradedResultView = {
  compileOutput?: string | null;
  testResults?: StudentTestResult[] | null;
  hidden?: { passedCount: number; totalCount: number } | null;
};

export function VerdictChip({
  status,
  verdict,
}: {
  status: GradedStatus;
  verdict: JudgeVerdict | null | undefined;
}) {
  const t = useTranslations("judge");

  if (status !== "completed") {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-neutral-100 px-2.5 py-1 text-xs font-semibold text-neutral-600">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        {status === "running" ? t("statusRunning") : t("statusQueued")}
      </span>
    );
  }

  const tone = verdictTone(verdict);
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold",
        tone === "success" && "bg-emerald-50 text-emerald-700",
        tone === "warning" && "bg-amber-50 text-amber-700",
        tone === "error" && "bg-red-50 text-red-700",
        tone === "neutral" && "bg-neutral-100 text-neutral-600",
      )}
    >
      {tone === "success" ? (
        <CheckCircle2 className="h-3.5 w-3.5" />
      ) : (
        <XCircle className="h-3.5 w-3.5" />
      )}
      {verdict ? t(`verdict.${verdict}`) : t("statusQueued")}
    </span>
  );
}

/**
 * `EX-RTW04`: a test a partial-points checker scored below full, in amber with
 * its share. Its own component so only such a row reads the locale.
 */
function PartialTestChip({ idx, share }: { idx: number; share: number }) {
  const t = useTranslations("judge");
  const locale = useLocale();
  return (
    <span
      title={t("partialShare", { share: formatShare(share, locale) })}
      className="flex h-6 min-w-6 items-center justify-center rounded-md bg-amber-50 px-1.5 text-[11px] font-semibold text-amber-800"
    >
      {idx}
    </span>
  );
}

export function SubmissionResultDetail({ result }: { result: GradedResultView }) {
  const t = useTranslations("judge");
  // `EX-RTW03`: a run-twice submission's rows read as Run 1, Jury and Run 2 cells.
  const runTwiceRows = hasRunTwiceRows(result.testResults) ? result.testResults : null;
  const idleRow = result.testResults?.find((test) => test.idle) ?? null;

  return (
    <div className="space-y-2">
      {result.compileOutput ? (
        <pre className="max-h-36 overflow-auto rounded-lg bg-neutral-900 p-2.5 text-[11px] text-red-300 whitespace-pre-wrap">
          {result.compileOutput}
        </pre>
      ) : null}

      {runTwiceRows ? (
        <RunTwiceDetail rows={runTwiceRows} />
      ) : result.testResults && result.testResults.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {result.testResults.map((test) =>
            partialShareOf(test) !== null ? (
              <PartialTestChip key={test.idx} idx={test.idx} share={test.share ?? 0} />
            ) : (
            <span
              key={test.idx}
              title={test.idle ? t("interactive.idle") : t(`verdict.${test.verdict}`)}
              className={cn(
                "flex h-6 min-w-6 items-center justify-center rounded-md px-1.5 text-[11px] font-semibold",
                test.verdict === "accepted"
                  ? "bg-emerald-50 text-emerald-700"
                  : // Never run, because an earlier test failed: not a failure of its own.
                    test.verdict === "skipped"
                    ? "bg-neutral-100 text-neutral-400"
                    : "bg-red-50 text-red-700",
              )}
            >
              {test.idx}
            </span>
            ),
          )}
        </div>
      ) : null}

      {/* `EX-IAC01`: a time limit spent waiting on the interactor, and the usual reason. */}
      {idleRow ? (
        <div
          className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs leading-relaxed text-amber-950 motion-safe:animate-in motion-safe:fade-in"
          data-testid="idle-note"
        >
          <p className="font-semibold">{t("interactive.idleOnTest", { n: idleRow.idx })}</p>
          <p className="mt-0.5">{t("interactive.idleWhy")}</p>
        </div>
      ) : null}

      {result.hidden ? (
        <p className="text-[11px] text-[rgb(var(--muted-foreground))]">
          {t("hiddenSummary", {
            passed: result.hidden.passedCount,
            total: result.hidden.totalCount,
          })}
        </p>
      ) : null}
    </div>
  );
}
